import React, { useState } from 'react';
import { useEffect } from "react";
import '../styles/Contact.css';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_EMAILJS_SERVICE_ID';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_EMAILJS_TEMPLATE_ID';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_EMAILJS_PUBLIC_KEY';
const EMAILJS_TO_EMAIL = 'info@tqnyalbyanat.com';
const MAX_MESSAGE_LENGTH = 180;

function Contact() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        message: '',
    });
    const [statusMessage, setStatusMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'message' && value.length > MAX_MESSAGE_LENGTH) return;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const sendEmail = async () => {
        if (EMAILJS_SERVICE_ID.includes('YOUR_') ||
            EMAILJS_TEMPLATE_ID.includes('YOUR_') ||
            EMAILJS_PUBLIC_KEY.includes('YOUR_')) {
            throw new Error('EmailJS is not configured.');
        }

        const templateParams = {
            name: `${formData.firstName} ${formData.lastName}`,
            organization: formData.company,
            phone: formData.phone,
            email: formData.email,
            time: new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Riyadh' }),
            message: formData.message,
            subject: `Contact form submission from ${formData.firstName} ${formData.lastName}`,
            to_email: EMAILJS_TO_EMAIL,
        };

        const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                service_id: EMAILJS_SERVICE_ID,
                template_id: EMAILJS_TEMPLATE_ID,
                user_id: EMAILJS_PUBLIC_KEY,
                template_params: templateParams,
            }),
        });

        if (!response.ok) throw new Error('Failed to send message');
    };

    const resetForm = () => {
        setFormData({ firstName: '', lastName: '', email: '', company: '', phone: '', message: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatusMessage('');
        setIsSending(true);

        try {
            await sendEmail();
            setStatusMessage('Message sent successfully — we will be in touch soon!');
            resetForm();
        } catch (error) {
            setStatusMessage('Failed to send message. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    useEffect(() => {
        document.title = "Contact Us | TQNY ALBYANAT";
    }, []);

    return (
        <section className="cnt-page">
            <div className="cnt-container">
                <div className="cnt-breadcrumb">
                    <a href="/">Home</a>
                    <span className="cnt-breadcrumb-sep">/</span>
                    <span className="cnt-breadcrumb-current">Contact</span>
                </div>
                <div className="cnt-grid">
                    {/* Left Side - Text and Info */}
                    <div>
                        <div className="cnt-eyebrow">Let's Connect</div>
                        <h1>Let's start a conversation.</h1>
                        <p className="cnt-lead">
                            Whether you're scoping a new initiative, exploring partnership, or simply curious about how we work — we'd be glad to hear from you. Most messages receive a response within one business day.
                        </p>
                        <div className="cnt-info">
                            <div className="cnt-info-card">
                                <div className="cnt-info-label">General Inquiries</div>
                                <div className="cnt-info-content">
                                    <a href="mailto:info@tqnyalbyanat.com">info@tqnyalbyanat.com</a>
                                </div>
                                <div className="cnt-info-sub">For all client inquiries, project discussions, and partnership opportunities.</div>
                            </div>
                        </div>
                    </div>
                    {/* Right Side - Form */}
                    <div className="cnt-form-wrap">
                        <div className="cnt-form-header">
                            <div className="cnt-form-title">Send us a message</div>
                            <div className="cnt-form-desc">Fill out the form below and we'll get back to you shortly.</div>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="cnt-form-row">
                                <div className="cnt-form-group">
                                    <label className="cnt-form-label">First Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        placeholder='First Name'
                                        className="cnt-form-input"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="cnt-form-group">
                                    <label className="cnt-form-label">Last Name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        placeholder='Last Name'
                                        className="cnt-form-input"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="cnt-form-row">
                                <div className="cnt-form-group">
                                    <label className="cnt-form-label">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder='Email'
                                        className="cnt-form-input"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="cnt-form-group">
                                    <label className="cnt-form-label">Company</label>
                                    <input
                                        type="text"
                                        name="company"
                                        placeholder='Company/Organization'
                                        className="cnt-form-input"
                                        value={formData.company}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="cnt-form-group">
                                <label className="cnt-form-label">Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder='+11 1234567890'
                                    className="cnt-form-input"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="cnt-form-group">
                                <label className="cnt-form-label">Message</label>
                                <textarea
                                    name="message"
                                    placeholder='Enter your enquiry here (max 180 characters)'
                                    className="cnt-form-textarea"
                                    rows="5"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <button type="submit" className="cnt-form-submit" disabled={isSending}>
                                {isSending ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                        {statusMessage && <p className="cnt-form-note">{statusMessage}</p>}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Contact;